// Dummy data for the Visitor & Contractor module — RON-5466.
//
// The module is being built design-first: every screen renders from the fixtures here so the
// UI can be reviewed before the APIs exist. Endpoints will be supplied later; at that point
// `VisitorAPI.jsx` swaps its fixture reads for real `post(...)` calls and this file shrinks to
// test fixtures only.
//
// Values mirror `web-visitor-manager.html` (the RON-5466 prototype) so the React build and the
// signed-off prototype can be compared side by side.

// ── The single switch ────────────────────────────────────────────────────────────
// While true, the module reads entitlement and host rights from the mocks below instead of
// from real storage/API values. Flip to false once `HomeScreenBookings_api` carries the
// entitlement flags and RON-5463 exposes host rights — nothing else needs to change.
export const USE_VISITOR_MOCKS = false;

// ── Entitlement (tblcompany_property) ────────────────────────────────────────────
// Shape matches `getVisitorEntitlement()` in `src/utils/visitorEntitlement.js`.
export const MOCK_ENTITLEMENT = {
  visitorPortal: true,
  contractorPortal: true,
  maxSites: 4,
};

// ── Host invite rights (tblvisitor_host_permission, RON-5463) ────────────────────
// The prototype calls this `ME`. Five rights are granted per employee or per group:
//   canVisitor    may invite visitors
//   canContractor may invite vetted contractors
//   canParking    may assign a parking space to a guest
//   canDesk       may assign a desk to a guest
//   canReception  may open the reception dashboard
//
// `canReception` is read but deliberately unused for now — Reception is becoming a separate
// front-end shared by admin (CI-3) and member (React) on the Laravel API base, so this app
// should not link to it until that URL exists.
export const MOCK_HOST_PERMISSIONS = {
  canVisitor: true,
  canContractor: true,
  canParking: true,
  canDesk: true,
  canReception: true,
};

// ── Visits (tblvisitor_visit) ────────────────────────────────────────────────────
//
// WIRE FORMAT (snake_case, nested), not the flat camelCase the screens use. Converted
// 1-October-2026, when the module was pointed at the real API.
//
// This is the Reception module's pattern, and it is here for the reason that one proved: when the
// fixture is already shaped like the server's answer, EVERY read goes through `visitorApiMapper`
// — mocked or live — so the mapper cannot silently drift from the contract. A fixture in the
// screens' own shape lets the mock bypass the translation entirely, which means a mapping bug
// only ever surfaces against a real server.
//
// Shapes follow RON-6002 §4.5: `visitor`, `site`, `building` and `host` are objects, times
// carry seconds, and `group_id` is the invitation's id while `group` (absent here) is the list
// of other people on it.
//
// `can_edit` / `can_cancel` are NOT in the fixture. They are the server's judgement, so the mock
// stamps them per status in `VisitorAPI.jsx` exactly as the real endpoint does.
//
// `itProvided` was dropped in the wire-format conversion — dummy by agreement, no backing
// column anywhere, never sent by the real API. The View screen's "IT equipment" row read "No"
// for everybody as a result, and the row itself was REMOVED on 07-October-2026 by product
// decision. Nothing reads the field now.
//
// `activity` stays: RON-6002 Q4 confirms a short trail rides on the single-visit read. The exact
// key names are still unconfirmed, which is why the mapper reads both plausible spellings.
export const MOCK_VISITS = [
  {
    "id": "v-sarah",
    "booking_code": "VIS-2026-0407-001",
    "kind": "visitor",
    "type": "standard",
    "status": "expected",
    "visitor": {
      "name": "Sarah Mitchell",
      "email": "s.mitchell@acmecorp.com",
      "company": "Acme Corp"
    },
    "purpose": null,
    "start_date": "2026-04-07",
    "end_date": "2026-04-07",
    "arrival_time": "09:30:00",
    "expected_out": "17:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": {
      "id": "bld-block-a",
      "name": "Block A"
    },
    "group_id": "g-acme",
    "host": {
      "name": "Michael Walsh"
    },
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-08-20T09:12",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-james",
    "booking_code": "VIS-2026-0408-002",
    "kind": "visitor",
    "type": "vip",
    "status": "expected",
    "visitor": {
      "name": "James O'Brien",
      "email": "j.obrien@vertexltd.com",
      "company": "Vertex Ltd"
    },
    "purpose": null,
    "start_date": "2026-04-08",
    "end_date": "2026-04-09",
    "arrival_time": "11:00:00",
    "expected_out": "16:30:00",
    "site": {
      "id": "site-london",
      "name": "London Office",
      "timezone": "Europe/London"
    },
    "building": null,
    "group_id": "g-acme",
    "host": {
      "name": "Michael Walsh"
    },
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-08-20T09:12",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-priya",
    "booking_code": "VIS-2026-0409-003",
    "kind": "visitor",
    "type": "standard",
    "status": "expected",
    "visitor": {
      "name": "Priya Sharma",
      "email": "p.sharma@novatech.com",
      "company": "Nova Tech"
    },
    "purpose": null,
    "start_date": "2026-04-09",
    "end_date": "2026-04-09",
    "arrival_time": "14:00:00",
    "expected_out": "18:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": {
      "id": "bld-block-b",
      "name": "Block B"
    },
    "group_id": "g-acme",
    "host": {
      "name": "Michael Walsh"
    },
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-08-20T09:12",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-declan",
    "booking_code": "CON-2026-0410-004",
    "kind": "contractor",
    "type": "standard",
    "status": "expected",
    "visitor": {
      "name": "Declan Murphy",
      "email": "declan@murphyelectrical.ie",
      "company": "Murphy Electrical Ltd"
    },
    "purpose": null,
    "start_date": "2026-04-10",
    "end_date": "2026-04-10",
    "arrival_time": "08:00:00",
    "expected_out": "17:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": {
      "id": "bld-block-a",
      "name": "Block A"
    },
    "group_id": "g-solo-declan",
    "host": {
      "name": "Michael Walsh"
    },
    "is_provisional": false,
    "trade": "Electrician",
    "vetted_until": "2026-12-31",
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-08-18T14:40",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-aoife",
    "booking_code": "CON-2026-0413-005",
    "kind": "contractor",
    "type": "standard",
    "status": "expected",
    "visitor": {
      "name": "Aoife Ryan",
      "email": "aoife@ryanfacilities.ie",
      "company": "Ryan Facilities"
    },
    "purpose": null,
    "start_date": "2026-04-13",
    "end_date": "2026-04-13",
    "arrival_time": "09:00:00",
    "expected_out": "15:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": null,
    "group_id": "g-solo-aoife",
    "host": {
      "name": "Michael Walsh"
    },
    "is_provisional": true,
    "trade": "Facilities Manager",
    "vetted_until": null,
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-08-19T11:05",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-tom",
    "booking_code": "VIS-2026-0328-006",
    "kind": "visitor",
    "type": "standard",
    "status": "cancelled",
    "visitor": {
      "name": "Tom Gallagher",
      "email": "t.gallagher@bluewave.io",
      "company": "BlueWave"
    },
    "purpose": null,
    "start_date": "2026-03-28",
    "end_date": "2026-03-28",
    "arrival_time": "10:00:00",
    "expected_out": "12:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": {
      "id": "bld-block-a",
      "name": "Block A"
    },
    "group_id": "g-solo-tom",
    "host": {
      "name": "Michael Walsh"
    },
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-03-20T10:30",
        "actor": "Michael Walsh"
      },
      {
        "action": "visit.cancel",
        "at": "2026-03-27T16:02",
        "actor": "Michael Walsh"
      }
    ]
  },
  {
    "id": "v-lin",
    "booking_code": "VIS-2026-0325-007",
    "kind": "visitor",
    "type": "standard",
    "status": "checked_in",
    "visitor": {
      "name": "Lin Wei",
      "email": "l.wei@orbitsystems.com",
      "company": "Orbit Systems"
    },
    "purpose": null,
    "start_date": "2026-03-25",
    "end_date": "2026-03-26",
    "arrival_time": "13:30:00",
    "expected_out": "17:30:00",
    "site": {
      "id": "site-newyork",
      "name": "New York Office",
      "timezone": "America/New_York"
    },
    "building": {
      "id": "bld-tower",
      "name": "Tower"
    },
    "group_id": "g-solo-lin",
    "host": {
      "name": "Michael Walsh"
    },
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-03-10T08:55",
        "actor": "Michael Walsh"
      },
      {
        "action": "visit.checkin",
        "at": "2026-03-25T13:34",
        "actor": "Reception"
      }
    ]
  },
  {
    "id": "v-nuala",
    "booking_code": "CON-2026-0320-008",
    "kind": "contractor",
    "type": "standard",
    "status": "checked_out",
    "visitor": {
      "name": "Nuala Brady",
      "email": "nuala@apexhvac.ie",
      "company": "Apex HVAC"
    },
    "purpose": null,
    "start_date": "2026-03-20",
    "end_date": "2026-03-20",
    "arrival_time": "08:30:00",
    "expected_out": "16:00:00",
    "site": {
      "id": "site-dublin",
      "name": "Dublin HQ",
      "timezone": "Europe/Dublin"
    },
    "building": {
      "id": "bld-block-b",
      "name": "Block B"
    },
    "group_id": "g-solo-nuala",
    "host": {
      "name": "Michael Walsh"
    },
    "is_provisional": false,
    "trade": "HVAC engineer",
    "vetted_until": "2026-09-30",
    "activity": [
      {
        "action": "visit.create",
        "at": "2026-03-05T15:20",
        "actor": "Michael Walsh"
      },
      {
        "action": "visit.checkin",
        "at": "2026-03-20T08:28",
        "actor": "Reception"
      },
      {
        "action": "visit.checkout",
        "at": "2026-03-20T15:47",
        "actor": "Reception"
      }
    ]
  }
];

// ── Sites and buildings (§4.2) ───────────────────────────────────────────────────
//
// Wire format. `location` is "a pair or null, never half a coordinate" — `(float) null` is
// `0.0`, a real point in the Gulf of Guinea, so half a pair would draw a pin in the sea rather
// than show a visible error.
export const MOCK_SITES = [
  {
    "id": "site-dublin",
    "name": "Dublin HQ",
    "address": "North Wall Quay, Dublin 1",
    "timezone": "Europe/Dublin",
    "location": {
      "lat": 53.3478,
      "lng": -6.2297
    },
    "building_count": 2,
    "buildings": [
      {
        "id": "bld-block-a",
        "name": "Block A",
        "is_default": true,
        "location": null
      },
      {
        "id": "bld-block-b",
        "name": "Block B",
        "is_default": false,
        "location": null
      }
    ]
  },
  {
    "id": "site-london",
    "name": "London Office",
    "address": "10 Bishopsgate, London EC2",
    "timezone": "Europe/London",
    "location": {
      "lat": 51.5155,
      "lng": -0.081
    },
    "building_count": 0,
    "buildings": []
  },
  {
    "id": "site-newyork",
    "name": "New York Office",
    "address": "New York, NY",
    "timezone": "America/New_York",
    "location": {
      "lat": 40.7128,
      "lng": -74.006
    },
    "building_count": 1,
    "buildings": [
      {
        "id": "bld-tower",
        "name": "Tower",
        "is_default": true,
        "location": null
      }
    ]
  },
  {
    "id": "site-berlin",
    "name": "Berlin Office",
    "address": "Berlin",
    "timezone": "Europe/Berlin",
    "location": {
      "lat": 52.52,
      "lng": 13.405
    },
    "building_count": 0,
    "buildings": []
  }
];

// ── Contractor register (§4.3) ───────────────────────────────────────────────────
//
// Wire format. `vetting` is an object with `state` (started | waiting | review | vetted |
// expired) and `until`.
//
// `documents` and `blocked_reason` are fixture-only: §4.3 is explicit that the register read
// carries neither documents nor vetting history. They are kept so the contractor-gate screens
// still have something to render in design review, and the mock must not serve them from the
// list endpoint.
export const CONTRACTOR_DB = [
  {
    "id": "c-declan",
    "name": "Declan Murphy",
    "email": "declan@murphyelectrical.ie",
    "company": "Murphy Electrical Ltd",
    "trade": "Electrician",
    "vetting": {
      "state": "vetted",
      "until": "2027-06-30"
    },
    "documents": [
      {
        "type": "insurance",
        "name": "Public liability insurance",
        "expires_on": "2027-03-31"
      },
      {
        "type": "safety",
        "name": "Safe Pass card",
        "expires_on": "2028-01-14"
      },
      {
        "type": "method",
        "name": "Method statement — rewiring",
        "expires_on": null
      }
    ]
  },
  {
    "id": "c-fiona",
    "name": "Fiona Keane",
    "email": "fiona@keanewelding.ie",
    "company": "Keane Welding",
    "trade": "Welder",
    "vetting": {
      "state": "vetted",
      "until": "2026-09-15"
    },
    "documents": [
      {
        "type": "insurance",
        "name": "Public liability insurance",
        "expires_on": "2026-09-01"
      },
      {
        "type": "safety",
        "name": "Hot works certification",
        "expires_on": "2026-11-20"
      }
    ]
  },
  {
    "id": "c-aoife",
    "name": "Aoife Ryan",
    "email": "aoife@ryanfacilities.ie",
    "company": "Ryan Facilities",
    "trade": "Facilities Manager",
    "vetting": {
      "state": "waiting",
      "until": null
    },
    "documents": [
      {
        "type": "insurance",
        "name": "Public liability insurance",
        "expires_on": "2027-01-31"
      }
    ]
  },
  {
    "id": "c-brendan",
    "name": "Brendan Cullen",
    "email": "brendan@apexroofing.ie",
    "company": "Apex Roofing",
    "trade": "Roofer",
    "vetting": {
      "state": "expired",
      "until": null
    },
    "documents": [],
    "blocked_reason": "Site safety incident, March 2026. Contact the compliance admin team before re-inviting."
  },
  {
    // EXPIRED WITH A DATE AND NO `blocked_reason`, which is the ordinary lapsed clearance and was
    // not representable until 29-October-2026: the only expired fixture carried a hand-written
    // `blocked_reason`, which wins over everything, so the dated reason line
    // ("…clearance expired on 30 Nov 2025") could not be seen on this screen at all.
    "id": "c-seamus",
    "name": "Seamus Byrne",
    "email": "seamus@byrnefitout.ie",
    "company": "Byrne Fit-Out Ltd",
    "trade": "Joiner",
    "vetting": {
      "state": "expired",
      "until": "2025-11-30"
    },
    "documents": []
  },
  {
    "id": "c-nuala",
    "name": "Nuala Brady",
    "email": "nuala@apexhvac.ie",
    "company": "Apex HVAC",
    "trade": "HVAC engineer",
    "vetting": {
      "state": "vetted",
      "until": "2026-09-30"
    },
    "documents": [
      {
        "type": "insurance",
        "name": "Public liability insurance",
        "expires_on": "2026-07-31"
      },
      {
        "type": "safety",
        "name": "Safe Pass card",
        "expires_on": "2027-05-05"
      }
    ]
  }
];

// Declined vetting requests — §4.4's `vetting_requests`, added 29-October-2026.
//
// THIS IS THE ONLY NOTICE THE HOST EVER GETS. §4.10: "No email is sent on decline — this entry is
// how the host finds out." A request that is declined and not shown here simply vanishes, and the
// host waits for a vetting that will never happen.
//
// Row shape is endpoint 10's reply with `status: "declined"`, `closed_at` and `decline_reason`.
// The reason is OPTIONAL on the admin side, so one fixture has none: a host can be told no and
// not told why, and the screen has to read sensibly either way.
export const MOCK_DECLINED_VETTING = [
  {
    id: "vet-req-declined-1",
    type: "unknown",
    status: "declined",
    email: "tom@brightspark.ie",
    company: "BrightSpark Electrical",
    contact_name: "Tom Bright",
    trade: "Electrician",
    reason: "Replacing the 3rd-floor distribution board; on site ~2 days.",
    needed_by: "2026-11-12",
    contractor_id: null,
    requested_by: "Michael Walsh",
    requested_at: "2026-10-26 09:05:00",
    repeat_count: 0,
    closed_at: "2026-10-28 14:20:00",
    decline_reason: "Use the framework contractor for distribution board work.",
  },
  {
    id: "vet-req-declined-2",
    type: "expired",
    status: "declined",
    email: "grace@finelinepainting.ie",
    company: "Fineline Painting",
    contact_name: "Grace Tobin",
    trade: "Painter",
    reason: "Repainting the east stairwell.",
    needed_by: null,
    contractor_id: null,
    requested_by: "Michael Walsh",
    requested_at: "2026-10-20 11:40:00",
    repeat_count: 1,
    closed_at: "2026-10-22 08:15:00",
    decline_reason: null,
  },
];
